import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Center, Spinner, Text, Box, IconButton } from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { loadConversation, getOrganizationName } from "../services/backend";
import DemoAnalysisView from "./DemoAnalysisView";
import UNTUCKitAnalysisView from "./UNTUCKitAnalysisView";
import ArcteryxAnalysisView from "./ArcteryxAnalysisView";

export default function AnalysisView() {
  const { conversation_id } = useParams();
  const navigate = useNavigate();
  const [conversationData, setConversationData] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both conversation and organization in parallel
        const [conversation, orgName] = await Promise.all([
          loadConversation(conversation_id),
          getOrganizationName(),
        ]);

        setConversationData(conversation);
        setOrganization(orgName);
      } catch (error) {
        console.error("Error loading data:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [conversation_id]);

  // Function to determine which analysis view to show based on organization
  const getAnalysisViewComponent = () => {
    if (organization) {
      switch (organization.toLowerCase()) {
        case "arcteryx":
        case "arc-teryx":
          return <ArcteryxAnalysisView conversationData={conversationData} />;
        case "untuckit":
        case "untuck-it":
          return <UNTUCKitAnalysisView conversationData={conversationData} />;
        default:
          return <DemoAnalysisView conversationData={conversationData} />;
      }
    }

    // Default fallback
    return <DemoAnalysisView conversationData={conversationData} />;
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="100vh">
        <Text color="red.500">Error loading conversation: {error}</Text>
      </Center>
    );
  }

  if (!conversationData || !conversationData.analysis) {
    return (
      <Center h="100vh">
        <Text>No analysis data available</Text>
      </Center>
    );
  }

  // Render the appropriate analysis view based on organization with back navigation
  return (
    <Box position="relative" w="100%" h="100%">
      {/* Back Navigation */}
      <Box position="fixed" top={24} left={6} zIndex={10}>
        <IconButton
          icon={<ArrowBackIcon />}
          aria-label="Back to Conversations"
          variant="ghost"
          size="lg"
          onClick={() => navigate("/conversations")}
          _hover={{
            bg: "gray.100",
            transform: "translateX(-2px)",
          }}
          _active={{
            bg: "gray.200",
          }}
          transition="all 0.2s ease"
          borderRadius="full"
          color="gray.600"
        />
      </Box>

      {/* Analysis Content */}
      {getAnalysisViewComponent()}
    </Box>
  );
}
